<template>
  <div
    ref="content"
    data-component="base-sortable-list"
    :class="{ 'base-sortable-list--scrollable': isScrollable }"
    @scroll="onScroll"
  >
    <div
      v-for="(item, index) in model"
      :key="item.vforkey"
      :draggable="isReordable && !handle"
      class="base-sortable-list-item"
    >
      <slot
        name="item"
        v-bind="{
          index,
          props: item,
          items: model,
          deleteItem: deleteItem,
          addItem: addItem,
        }"
      />
    </div>
    <slot
      name="footer"
      :add-item="addItem"
    />
  </div>
</template>

<script lang="ts">
import { v4 as uuidv4 } from 'uuid';
import Sortable, { SortableEvent } from 'sortablejs';
import { computed, defineComponent, PropType, ref, watch } from 'vue-demi';

import '@onereach/styles/scrollbar.css';

const LIST_ANIMATION_PARAMS = {
  animation: 250,
  easing: 'cubic-bezier(0.12, 0.39, 0.5, 0.93)',
};

type BasisComponentType = { vforkey: string; };

function getItemWithUUID(data: Record<string, any>): BasisComponentType {
  return 'vforkey' in data ? data as BasisComponentType : {
    vforkey: uuidv4(),
    ...data,
  };
}

export default defineComponent({
  name: 'BaseSortableList',
  props: {
    list: {
      type: Array as PropType<BasisComponentType[]>,
      default: () => ([]),
    },
    isReordable: {
      type: Boolean,
      default: true,
    },
    isScrollable: {
      type: Boolean,
      default: true,
    },
    isDisabled: Boolean,
    handle: {
      type: String,
      default: '',
    },
    itemProps: {
      type: Object,
      default: () => ({}),
    },
  },

  emits: [
    'scroll',
    'update',
    'item-before-add',
    'item-added',
    'item-before-delete',
    'item-deleted',
  ],

  setup(props, ctx) {
    let sortable: Sortable | undefined;
    const content = ref<HTMLElement>();

    const model = computed<BasisComponentType[]>({
      get() {
        return props.list.map(item => getItemWithUUID(item));
      },
      set(value: BasisComponentType[]) {
        ctx.emit('update', value);
      },
    });

    const handle = computed(() => {
      return props.handle ?? '[data-component="base-sortable-list"]';
    });

    watch([content, () => props.isReordable], ([content, canReorder]) => {
      sortable?.destroy();
      sortable = undefined;
      if (content && canReorder) {
        sortable = Sortable.create(content, {
          ...LIST_ANIMATION_PARAMS,
          handle: handle.value,
          disabled: props.isDisabled,
          onUpdate,
        });
      }
    });

    function onUpdate(event: SortableEvent) {
      if (props.isDisabled) return;
      const nextModelValue = [...model.value];
      const reorderedItem = nextModelValue.splice(event.oldIndex!, 1)[0];
      nextModelValue.splice(event.newIndex!, 0, reorderedItem);
      model.value = [...nextModelValue];
    }

    function addItem() {
      if (props.isDisabled) return;
      ctx.emit('item-before-add', model.value);
      const newItem = getItemWithUUID(props.itemProps);
      const result = [...model.value, newItem];
      ctx.emit('item-added', newItem);
      model.value = result;
    }

    function deleteItem(index: number): void {
      if (props.isDisabled) return;
      ctx.emit('item-before-delete', model.value);
      const item = model.value[index];
      const modelValue = [...model.value];
      modelValue.splice(index, 1);
      ctx.emit('item-deleted', item);
      model.value = modelValue;
    }

    function onScroll(event: MouseEvent) {
      ctx.emit('scroll', event);
    }

    return {
      model,
      content,
      onScroll,
      addItem,
      deleteItem,
    };
  },
});
</script>

<style lang="scss" scoped>
[data-component='base-sortable-list'] {
  &.base-sortable-list--scrollable {
    height: 100%;
    overflow: auto;
  }
}
</style>
